import { AffectedEntityType, EventState } from '@bp/planung-graphql-types';
import { extractClassGroupNamesFromEvent } from '@bp/shared-planung-utils';
import {
  AccessIcon,
  ActionBar,
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Card,
  Dropdown,
  DropdownMenu,
  EditIcon,
  EmptyState,
  Grid,
  GridColumn,
  GridRow,
  InvisibleIcon,
  PrintIcon,
  Row,
  SpinnerIcon,
  TextArea,
  TimetableView,
  TimetableViewEntryType,
  UploadIcon,
  VisibleIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomFilter, CustomFilterEntry } from '../../../../components/CustomFilter/CustomFilter';
import { SubstitutionFormRowStatus } from '../../../../components/Substitutions/forms/SubstitutionFormRow';
import { CancelEventModal } from '../../../../components/Substitutions/modals/CancelEventModal';
import { DifferentTimeModal } from '../../../../components/Substitutions/modals/DifferentTimeModal';
import { MergeEventModal } from '../../../../components/Substitutions/modals/MergeEventModal';
import { SubstitutionAnnouncementsModal } from '../../../../components/Substitutions/modals/SubstitutionAnnouncementsModal';
import { SubstitutionModal } from '../../../../components/Substitutions/modals/SubstitutionModal';
import { WorkOrderModal } from '../../../../components/Substitutions/modals/WorkOrderModal';
import { useSubstitutionStore } from '../../../../components/Substitutions/SubstitutionProvider';
import {
  SubstitutionEventsTable,
  SubstitutionEventsTableType,
} from '../../../../components/Substitutions/tables/SubstitutionEventsTable';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import {
  SortDirection,
  SubstitutionReason,
  use_EventsQuery,
  use_SchoolEventsQuery,
  use_SetPrintEventMutation,
  use_SubstitutionFilterTimetableVersionsQuery,
  use_TimetableVersionsQuery,
  useSubstitutionAnnouncementsQuery,
  useTimeGridEntriesByTimeGridQuery,
} from '../../../../types/planung-graphql-client-defs';
import { apiHelper } from '../../../../utils/apiHelper';
import { normalizedDate } from '../../../../utils/dateCalculations';
import { getTextForSubstitutionReason } from '../../../../utils/events';
import { downloadSubstitutionReport, downloadSubstitutionStudentReport } from '../../../../utils/substitutionReport';
import { showSuccessToast } from '../../../../utils/toast';
import styles from './PlanSubstitutions.module.scss';
import {
  createMapFromArray,
  mapSubstitutionTeachers,
  mapTeachers,
  parseState,
  SubstitutionDataEntry,
  SubstitutionDataMissingTeacherEntry,
} from './utils';

export type SubstitutionDataGroupEntry = SubstitutionDataEntry & {
  length: number;
  index: number;
};

export type SubstitutionDataHighlightEntry = {
  highlightUuid: string;
  highlightName: string;
  teacherUuid: string;
  teacherName: string;
  status: Extract<SubstitutionFormRowStatus, 'on-school-event' | 'signed-out' | 'attendance'>;
  start: Dayjs;
  end: Dayjs;
  renderStart: Dayjs;
  renderEnd: Dayjs;
  fullDay: boolean;
  inTimegrid: boolean;
  groupClassNames?: string;
};

export const PlanSubstitutions = observer(() => {
  const { t } = useTranslation();
  const perms = usePermissionChecker();
  const userStore = useUserConfigContext();
  const substitutionStore = useSubstitutionStore();
  const currentSchoolYear = userStore.selectedSchoolYear;
  const timeGridContext = useMemorizedCacheTag('TIME_GRID');
  const eventContext = useMemorizedCacheTag('EVENT');
  const schoolEventContext = useMemorizedCacheTag('SCHOOL_EVENT');
  const announcementContext = useMemorizedCacheTag('SUBSTITUTION_ANNOUNCEMENTS');
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims?.getOrganizationUuid();

  const selectedDay = substitutionStore.selectedDay;
  const dateString = substitutionStore.dateString;

  // selected day in school year
  const inSchoolYear = selectedDay.isBetween(
    dayjs(currentSchoolYear?.start),
    dayjs(currentSchoolYear?.end),
    'day',
    '[]',
  );

  const relevantMax = useRef<number>(0);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [announcementsCollapsed, setAnnouncementsCollapsed] = useState<boolean>(true);
  const [selectedClassEntries, setSelectedClassEntries] = useState<SubstitutionDataEntry[]>([]);
  const [selectedClassIndex, setSelectedClassIndex] = useState<number>(0);
  const [selectedEvent, setSelectedEvent] = useState<SubstitutionEventsTableType | null>(null);
  const [customFilter, setCustomFilter] = useState<CustomFilterEntry[]>([]);

  const [substitutionModalOpen, setSubstitutionModalOpen] = useState<boolean>(false);
  const [cancelEventModalOpen, setCancelEventModalOpen] = useState<boolean>(false);
  const [mergeEventModalOpen, setMergeEventModalOpen] = useState<boolean>(false);
  const [workOrderModalOpen, setWorkOrderModalOpen] = useState<boolean>(false);
  const [differentTimeModalOpen, setDifferentTimeModalOpen] = useState<boolean>(false);
  const [announcementsModalOpen, setAnnouncementsModalOpen] = useState<boolean>(false);

  const teacherEventsRef = useRef<Map<string, SubstitutionEventsTableType[]>>(new Map());

  const selectedDayStartString = selectedDay.startOf('day').utc().toISOString();
  const selectedDayEndString = selectedDay.endOf('day').utc().toISOString();

  const [, updatePrint] = use_SetPrintEventMutation();

  const [{ data: timeGridEntriesData }] = useTimeGridEntriesByTimeGridQuery({
    context: timeGridContext,
    pause: !selectedDay,
  });

  const timetableStart =
    timeGridEntriesData && timeGridEntriesData.timeGridEntries[0]
      ? dayjs(timeGridEntriesData?.timeGridEntries[0].start).startOf('hour')
      : dayjs().set('hour', 6);

  const timetableEnd =
    timeGridEntriesData && timeGridEntriesData.timeGridEntries[timeGridEntriesData.timeGridEntries.length - 1]
      ? dayjs(timeGridEntriesData?.timeGridEntries[timeGridEntriesData.timeGridEntries.length - 1].end).endOf('hour')
      : dayjs().set('hour', 18);

  // normalize date so that it is compareable
  const normalizedTimetableStart = normalizedDate(timetableStart, selectedDay);
  const normalizedTimetableEnd = normalizedDate(timetableEnd, selectedDay);

  const [{ data: eventData }, refetch] = use_EventsQuery({
    variables: {
      options: {
        sort: [{ isSchoolEvent: SortDirection.Desc }, { start: SortDirection.Asc }],
      },
      where: {
        isSchoolEvent: false,
        end_GTE: selectedDayStartString,
        start_LTE: selectedDayEndString,
      },
    },
    context: eventContext,
    pause: !selectedDay,
  });

  const { classesData, subjectData, teacherData, groupsData, divisionsData, roomsData, gradeGroupData } =
    useLoadBasicData({
      pause: false,
    });

  const [{ data: eventDataSchoolEvents }] = use_SchoolEventsQuery({
    variables: {
      organizationUuid,
      from: dateString,
      to: dateString,
    },
    context: schoolEventContext,
  });

  const [{ data: announcementsData }] = useSubstitutionAnnouncementsQuery({
    variables: { selectedDay: dateString },
    context: announcementContext,
    pause: !selectedDay,
  });

  // blocks holen und
  // nur block aus selected date und davon timetables
  const [{ data: timetableVersionsData }] = use_SubstitutionFilterTimetableVersionsQuery({
    variables: {
      where: {
        active: true,
        timetable: {
          timetableBlocks_SOME: {
            end_GTE: selectedDayStartString,
            start_LTE: selectedDayEndString,
          },
        },
      },
    },
    pause: !selectedDay,
  });

  const publicAnnouncement = announcementsData?.substitutionAnnouncements[0]?.publicAnnouncement ?? '';
  const internalAnnouncement = announcementsData?.substitutionAnnouncements[0]?.internalAnnouncement ?? '';

  const lookupMaps = useMemo(() => {
    return {
      classMap: createMapFromArray(classesData?.classes ?? []),
      teacherMap: createMapFromArray(teacherData?.people ?? []),
      subjectMap: createMapFromArray(subjectData?.subjects ?? []),
      groupMap: createMapFromArray(groupsData?.groups ?? []),
      divisionMap: createMapFromArray(divisionsData?.divisions ?? []),
      roomMap: createMapFromArray(roomsData?.rooms ?? []),
    };
  }, [
    classesData?.classes,
    teacherData?.people,
    subjectData?.subjects,
    groupsData?.groups,
    divisionsData?.divisions,
    roomsData?.rooms,
  ]);

  const events: SubstitutionEventsTableType[] = useMemo(() => {
    if (!eventData?.events || !lookupMaps) return [];

    const { subjectMap, teacherMap, classMap, groupMap, divisionMap, roomMap } = lookupMaps;

    teacherEventsRef.current.clear();

    const mappedEvents: SubstitutionEventsTableType[] = eventData.events.map((event) => {
      const classesOrGroups = event.isSchoolEvent ? event.classesOrGroups : event.originalClassesOrGroups;
      const hasClassesOrGroups = event.classesOrGroups.length === 0;
      const subject = subjectMap.get(event.subject.uuid);
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);

      const aggregated = event.isAggregated ? eventData.events.find((e) => e.uuid === event.isAggregated?.uuid) : null;

      const teachers = mapTeachers(
        aggregated?.personsConnection || event.personsConnection,
        teacherMap,
        hasClassesOrGroups,
      );

      const substitutionTeachers = mapSubstitutionTeachers(event.personsConnection, teacherMap);

      const states = parseState(event.currentState);

      const missingTeachers: SubstitutionDataMissingTeacherEntry[] = states
        .filter((s) => {
          return (
            (s.state == EventState.PersonNotAvailable || s.state == EventState.PersonOnSchoolTrip) &&
            s.affectedEntityType == AffectedEntityType.Person
          );
        })
        .filter((value, index, array) => {
          return array.findIndex((v) => v.affectedEntityUuid === value.affectedEntityUuid) === index;
        })
        .map((s) => {
          const teacher = teacherMap.get(s.affectedEntityUuid as string);
          return {
            uuid: teacher?.uuid ?? '',
            name: teacher?.displayName ?? '',
            shortName: teacher?.shortName ?? '',
            status: s.affectedEntityType === 'OTHER_EVENT' ? 'on-school-event' : 'signed-out',
            fullDay: s.fullDay ?? false,
            state: s.state,
          };
        });

      const { groupClassNamesLong, groupClassNamesShort } = extractClassGroupNamesFromEvent({
        eventClassesOrGroups: classesOrGroups,
        data: { classesData: { classes: classesData.classes }, groupsData, divisionsData },
      });

      const { groupClassNamesLong: originalGroupClassNamesLong, groupClassNamesShort: originalGroupClassNamesShort } =
        extractClassGroupNamesFromEvent({
          eventClassesOrGroups: event.originalClassesOrGroups,
          data: { classesData: { classes: classesData.classes }, groupsData, divisionsData },
        });

      let aggregatedGroupClassNamesLong = groupClassNamesLong;
      let aggregatedGroupClassNamesShort = groupClassNamesShort;
      let aggregatedOriginalGroupClassNamesLong = null;
      let aggregatedOriginalGroupClassNamesShort = null;

      if (aggregated) {
        const aggregatedResult = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: aggregated.classesOrGroups,
          data: { classesData: { classes: classesData.classes }, groupsData, divisionsData },
        });
        aggregatedGroupClassNamesLong = aggregatedResult.groupClassNamesLong;
        aggregatedGroupClassNamesShort = aggregatedResult.groupClassNamesShort;

        const aggregatedOriginalResult = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: aggregated.originalClassesOrGroups,
          data: { classesData: { classes: classesData.classes }, groupsData, divisionsData },
        });
        aggregatedOriginalGroupClassNamesLong = aggregatedOriginalResult.groupClassNamesLong;
        aggregatedOriginalGroupClassNamesShort = aggregatedOriginalResult.groupClassNamesShort;
      }

      const allOriginalClasses = new Map<string, SubstitutionDataEntry>();

      classesOrGroups
        .filter((cg) => cg.__typename === 'Class')
        .forEach((c) => {
          const classData = classMap.get(c.uuid);
          if (classData && classData.uuid && classData.name && classData.shortName) {
            allOriginalClasses.set(c.uuid, {
              uuid: classData.uuid,
              name: classData.name,
              shortName: classData.shortName,
            });
          }
        });

      classesOrGroups
        .filter((cg) => cg.__typename === 'Group')
        .forEach((g) => {
          const group = groupMap.get(g.uuid);
          if (group?.divisionConnection?.edges) {
            group.divisionConnection.edges.forEach((edge) => {
              const division = divisionMap.get(edge.node.uuid);
              if (division?.class && division.class.uuid && division.class.name && division.class.shortName) {
                allOriginalClasses.set(division.class.uuid, {
                  uuid: division.class.uuid,
                  name: division.class.name,
                  shortName: division.class.shortName,
                });
              }
            });
          }
        });

      const classGroupUuidsOnSchoolTrip = states
        .filter((da) => da.state === EventState.ClassOnSchoolTrip || da.state === EventState.GroupOnSchoolTrip)
        .map((da) => da.affectedEntityUuid);

      const allClassesAndGroupsOnSchoolTrip = event.originalClassesOrGroups.every((c) => {
        return classGroupUuidsOnSchoolTrip.includes(c.uuid);
      });

      const doNotSubstitute = states.some((s) => {
        return s.state === EventState.NeverSubstituted;
      });

      const perparedEvent: SubstitutionEventsTableType = {
        uuid: event.uuid,
        onSchoolTrip: allClassesAndGroupsOnSchoolTrip,
        doNotSubstitute: doNotSubstitute,
        output: event.print,
        start: eventStart,
        end: eventEnd,
        involvedClasses: Array.from(allOriginalClasses.values()),
        groupClassNames: aggregatedGroupClassNamesShort,
        originalGroupClassNames: originalGroupClassNamesShort,
        groupClassNamesLong: aggregatedGroupClassNamesLong,
        originalGroupClassNamesLong: originalGroupClassNamesLong,
        aggregatedEventOriginalGroupClassNames: aggregatedOriginalGroupClassNamesShort,
        aggregatedEventOriginalGroupClassNamesLong: aggregatedOriginalGroupClassNamesLong,
        classes: classesOrGroups
          .filter((cg) => cg.__typename === 'Class')
          .map((c) => {
            const classData = classMap.get(c.uuid);
            return {
              uuid: classData?.uuid ?? '',
              name: classData?.name ?? '',
              shortName: classData?.shortName ?? '',
            };
          }),
        groups: classesOrGroups
          .filter((cg) => cg.__typename === 'Group')
          .map((g) => {
            const group = groupMap.get(g.uuid);
            const division = group?.divisionConnection?.edges?.[0]?.node.uuid
              ? divisionMap.get(group?.divisionConnection?.edges?.[0]?.node.uuid)
              : null;
            const edge = division?.groupsConnection?.edges.find((e) => e.node.uuid === group?.uuid);

            return {
              uuid: group?.uuid ?? '',
              name: `${group?.name ?? ''} (${division?.class?.name ?? ''})`,
              shortName: `${group?.shortName ?? ''} (${division?.class?.shortName ?? ''})`,
              length: division?.groupsConnection?.totalCount ?? 1,
              index: edge?.properties?.order ?? 0,
            };
          })
          .sort(),
        subject: {
          uuid: subject?.uuid ?? '',
          name: subject?.name ?? '',
          shortName: subject?.shortName ?? '',
        },
        rooms: (aggregated ?? event).rooms.map((r) => {
          const room = roomMap.get(r.uuid);
          return {
            uuid: room?.uuid ?? '',
            name: room?.name ?? '',
            shortName: room?.name ?? '',
          };
        }),
        originalRooms: event.originalRooms.map((r) => ({
          uuid: r.uuid,
          name: r.name,
          shortName: '',
        })),
        teachers,
        originalTeachers: event.originalPersons.map((p) => {
          const t = teacherMap.get(p.uuid);
          return {
            name: t?.displayName ?? '',
            uuid: t?.uuid ?? '',
            shortName: t?.shortName ?? '',
          };
        }),
        missingTeachers,
        substitutionTeachers,
        action: event.substitutionReasons ? getTextForSubstitutionReason(event.substitutionReasons) : '',
        comment: event.isSchoolEvent ? (event.comment ?? '') : (event.substitutionComment ?? ''),
        conflicts: JSON.parse(event.currentState ?? '[]'),
        isSchoolEvent: event.isSchoolEvent,
        substitutionReason: event.substitutionReasons ?? null,
        substitutionComment: event.substitutionComment ?? null,
        isCancelled: event.canceled || event.substitutionReasons === SubstitutionReason.Cancelled,
        timeGridEntryShortName: event.sourceTimeGridEntry?.shortName ?? null,
      };

      const eventTeachers = new Set<string>();
      teachers.forEach((t) => eventTeachers.add(t.uuid));
      substitutionTeachers.forEach((t) => eventTeachers.add(t.uuid));
      eventTeachers.forEach((teacherUuid) => {
        if (!teacherEventsRef.current.has(teacherUuid)) {
          teacherEventsRef.current.set(teacherUuid, []);
        }
        teacherEventsRef.current.get(teacherUuid)?.push({
          ...perparedEvent,
        });
      });
      return perparedEvent;
    });

    const groupedByStart = mappedEvents.reduce(
      (acc, event) => {
        const start = event.start.toISOString();
        if (!acc[start]) {
          acc[start] = [];
        }
        acc[start].push(event);
        return acc;
      },
      {} as Record<string, SubstitutionEventsTableType[]>,
    );

    const sortedEvents = Object.values(groupedByStart).map((group) =>
      group.sort((a, b) => {
        const aClass = a.groupClassNames || a.originalGroupClassNames || '';
        const bClass = b.groupClassNames || b.originalGroupClassNames || '';
        return aClass.localeCompare(bClass, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }),
    );

    const schoolEvents: SubstitutionEventsTableType[] =
      eventDataSchoolEvents?.schoolEvents.map((event) => {
        const teachers = event.persons.map((p) => {
          const teacher = teacherMap.get(p.uuid);
          return {
            uuid: teacher?.uuid ?? '',
            name: teacher?.displayName ?? p.listName,
            shortName: teacher?.shortName ?? '',
            status: 'normal' as const,
            givenSubstituteHours: null,
          };
        });

        const rooms = event.rooms.map((room) => {
          return {
            uuid: room?.uuid ?? '',
            name: room?.name ?? '',
            shortName: room?.name ?? '',
          };
        });

        const a: SubstitutionEventsTableType = {
          uuid: event.uuid,
          start: dayjs(event.start),
          output: true,
          end: dayjs(event.end),
          involvedClasses: [],
          groupClassNames: event.groupClassNames,
          originalGroupClassNames: event.groupClassNames,
          groupClassNamesLong: event.groupClassNamesLong,
          originalGroupClassNamesLong: event.groupClassNamesLong,
          classes: [],
          groups: [],
          subject: event.subject,
          rooms: rooms,
          teachers: teachers,
          missingTeachers: [],
          substitutionTeachers: [],
          action: '',
          comment: event.comment ?? '',
          conflicts: [],
          isSchoolEvent: true,
          substitutionReason: null,
          substitutionComment: null,
          isCancelled: false,
          aggregatedEventOriginalGroupClassNames: null,
          aggregatedEventOriginalGroupClassNamesLong: null,
          originalTeachers: teachers,
          originalRooms: rooms,
          onSchoolTrip: event.isClassTrip,
          doNotSubstitute: false,
          timeGridEntryShortName: null,
        };
        return a;
      }) ?? [];

    return [...schoolEvents, ...sortedEvents.flat()];
  }, [
    eventDataSchoolEvents?.schoolEvents,
    eventData?.events,
    classesData.classes,
    groupsData,
    divisionsData,
    lookupMaps,
  ]);

  const customFilterMaps = useMemo(() => {
    const gradeGroupFilterUuids =
      customFilter.find((c) => c.typeId === 'gradeGroups')?.values?.map((g) => g.value as string) ?? [];
    const timetableFilterUuids =
      customFilter.find((c) => c.typeId === 'timetables')?.values?.map((t) => t.value as string) ?? [];

    const timetableVersions =
      timetableVersionsData?.timetableVersions.filter((version) =>
        timetableFilterUuids.includes(version.timetable.uuid),
      ) ?? [];

    const versionClassesUuids = timetableVersions.flatMap((v) => v.classes.map((c) => c.uuid));

    return { gradeGroupFilterUuids, versionClassesUuids };
  }, [customFilter, timetableVersionsData?.timetableVersions]);

  const filteredEvents: SubstitutionEventsTableType[] = useMemo(() => {
    if (!events.length) return [];

    const { gradeGroupFilterUuids, versionClassesUuids } = customFilterMaps;

    const customFiltered = events.filter((e) => {
      if (gradeGroupFilterUuids.length > 0) {
        const classesGradeGroupUuids = e.classes
          .map((c) => classesData?.classes.find((cls) => cls.uuid === c.uuid)?.gradeGroup?.uuid)
          .filter(Boolean);

        const hasGradeGroup = classesGradeGroupUuids.some((uuid) => uuid && gradeGroupFilterUuids.includes(uuid));
        if (!hasGradeGroup) return false;
      }

      if (versionClassesUuids.length > 0) {
        const classesUuids = e.involvedClasses.map((i) => i.uuid);
        const isInTimetable = classesUuids.some((uuid) => versionClassesUuids.includes(uuid));
        if (!isInTimetable) return false;
      }

      return true;
    });

    const relevantFiltered = customFiltered.filter((e) => {
      // Schulveranstaltungen sind immer relevant
      if (e.isSchoolEvent) return true;

      // Events auf Schulfahrt sind nicht relevant
      if (e.onSchoolTrip || e.doNotSubstitute) return false;

      // Prüfe ob Event relevant ist
      const hasOutput = e.output;
      const hasSubstitutionReason = e.substitutionReason;
      const hasPersonConflicts = e.conflicts.some(
        (s) => s.state.includes(EventState.PersonNotAvailable) || s.state.includes(EventState.PersonMissing),
      );

      return hasOutput || hasSubstitutionReason || hasPersonConflicts;
    });

    return showAll ? customFiltered : relevantFiltered;
  }, [events, showAll, customFilterMaps, classesData?.classes]);

  relevantMax.current = events.filter((e) => {
    // Schulveranstaltungen sind immer relevant
    if (e.isSchoolEvent) return true;

    // Events auf Schulfahrt sind nicht relevant
    if (e.onSchoolTrip || e.doNotSubstitute) return false;

    // Prüfe ob Event relevant ist
    const hasOutput = e.output;
    const hasSubstitutionReason = e.substitutionReason;
    const hasPersonConflicts = e.conflicts.some(
      (s) => s.state.includes(EventState.PersonNotAvailable) || s.state.includes(EventState.PersonMissing),
    );

    return hasOutput || hasSubstitutionReason || hasPersonConflicts;
  }).length;

  useEffect(() => {
    const involvedClasses = filteredEvents.length > 0 ? filteredEvents[0].involvedClasses : [];

    setSelectedClassEntries((prevEntries) => {
      const newUuids = involvedClasses.map((c) => c.uuid).join(',');
      const prevUuids = prevEntries.map((c) => c.uuid).join(',');

      if (newUuids !== prevUuids) {
        return involvedClasses;
      }
      return prevEntries;
    });
  }, [filteredEvents[0]]);

  const timetableEntries: TimetableViewEntryType[] = useMemo(() => {
    if (!selectedClassEntries[selectedClassIndex]) return [];

    const entries: TimetableViewEntryType[] = [];
    const classEvents = events.filter(
      (s) =>
        s.involvedClasses.some((c) => c.uuid === selectedClassEntries[selectedClassIndex].uuid) &&
        s.substitutionReason !== SubstitutionReason.Cancelled &&
        s.substitutionReason !== SubstitutionReason.Aggregated,
    );

    for (const classEvent of classEvents) {
      const subject = subjectData?.subjects.find((s) => s.uuid === classEvent.subject.uuid);

      const entry = {
        uuid: classEvent.uuid,
        start: classEvent.start.toDate(),
        end: classEvent.end.toDate(),
        bgColor: subject?.timetableConfig?.color ?? undefined,
        field1: subject?.shortName,
        field4: Array.from(new Set(classEvent.teachers.map((t) => t.shortName))).join(', '),
        tooltip: {
          field1: subject?.name,
          field4: Array.from(new Set(classEvent.teachers.map((t) => t.name))).join(', '),
        },
        additionalData: classEvent.rooms,
      };

      if (classEvent.groups.length > 0) {
        classEvent.groups
          .filter((g) => {
            const selectedClass = classesData?.classes.find(
              (c) => c.uuid === selectedClassEntries[selectedClassIndex].uuid,
            );
            return selectedClass && g.shortName.includes(selectedClass.shortName);
          })
          .forEach((group) => {
            entries.push({
              ...entry,
              field2: group.shortName,
              tooltip: {
                ...entry.tooltip,
                field2: group.name,
              },
              group: {
                uuid: group.uuid,
                index: group.index ?? 0,
                length: group.length ?? 0,
              },
            });
          });
      } else {
        entries.push({
          ...entry,
          field2: Array.from(new Set(classEvent.classes.map((c) => c.shortName))).join(', '),
          tooltip: {
            ...entry.tooltip,
            field2: Array.from(new Set(classEvent.classes.map((c) => c.name))).join(', '),
          },
        });
      }
    }

    return entries;
  }, [selectedClassEntries, selectedClassIndex, classesData?.classes, subjectData?.subjects, events]);

  function handleClassSelection(direction: 'right' | 'left') {
    let newIndex = selectedClassIndex;
    if (direction === 'right') {
      newIndex = newIndex + 1 > selectedClassEntries.length - 1 ? 0 : newIndex + 1;
    } else {
      newIndex = newIndex - 1 < 0 ? selectedClassEntries.length - 1 : newIndex - 1;
    }
    setSelectedClassIndex(newIndex);
  }

  const selectEvent = useCallback(
    (uuid: string | null) => {
      if (uuid) {
        const event = events.find((s) => s.uuid === uuid);
        setSelectedEvent(event ?? null);
      } else {
        setSelectedEvent(null);
      }
    },
    [events],
  );

  const handleRowSelection = useCallback(
    (row: Row<SubstitutionEventsTableType>) => {
      substitutionStore.setSelectedRowIndex(row.id);
      const classes = events.find((e) => e.uuid === row.original.uuid)?.involvedClasses;
      if (classes) {
        setSelectedClassEntries(classes);
        setSelectedClassIndex(0);
      }
    },
    [events, substitutionStore],
  );

  const gradeGroupFilterOptions = useMemo(
    () =>
      gradeGroupData?.gradeGroups.map((gradeGroup) => ({
        value: gradeGroup.uuid,
        label: gradeGroup.name,
      })) ?? [],
    [gradeGroupData?.gradeGroups],
  );

  const timetableFilterOptions: { value: string; label: string }[] = useMemo(
    () =>
      timetableVersionsData?.timetableVersions.flatMap((version) => {
        return {
          value: version.timetable.uuid,
          label: version.timetable.name,
        };
      }) ?? [],
    [timetableVersionsData?.timetableVersions],
  );

  async function handleOutputChange(uuid: string, output: boolean) {
    await updatePrint(
      {
        eventUuid: uuid,
        print: output,
      },
      { context: eventContext },
    );
    refetch({ requestPolicy: 'network-only' });
  }

  function handleSubstitute(uuid: string) {
    selectEvent(uuid);
    setSubstitutionModalOpen(true);
  }

  function handleCancelEvent(uuid: string) {
    selectEvent(uuid);
    setCancelEventModalOpen(true);
  }

  function handleMergeEvent(uuid: string) {
    selectEvent(uuid);
    setMergeEventModalOpen(true);
  }

  function handleWorkOrder(uuid: string) {
    selectEvent(uuid);
    setWorkOrderModalOpen(true);
  }

  function handleDifferentTime(uuid: string) {
    selectEvent(uuid);
    setDifferentTimeModalOpen(true);
  }

  async function publishApp(date: string) {
    await apiHelper<void>({
      method: 'POST',
      path: `/app/publish/${date}`,
    });
    showSuccessToast(t('substitutions.published'));
  }

  const handleFilterChange = useCallback((entry: CustomFilterEntry) => {
    setCustomFilter((prevCustomFilter) => {
      const entries = [...prevCustomFilter];
      const newEntry = entries.find((e) => e.typeId === entry.typeId);
      if (newEntry) newEntry.values = entry.values;
      else entries.push(entry);
      return entries;
    });
  }, []);

  const customFilterEntries = useMemo(
    () => [
      {
        typeId: 'gradeGroups' as const,
        label: t('gradeGroups.title', { count: 2 }),
        options: gradeGroupFilterOptions,
        values: customFilter.find((c) => c.typeId === 'gradeGroups')?.values ?? [],
      },
      {
        typeId: 'timetables' as const,
        label: t('timetable.title', { count: 2 }),
        options: timetableFilterOptions,
        values: customFilter.find((c) => c.typeId === 'timetables')?.values ?? [],
      },
    ],
    [t, gradeGroupFilterOptions, timetableFilterOptions, customFilter],
  );

  if (!inSchoolYear) {
    return (
      <EmptyState
        size='big'
        icon={<AccessIcon />}
        title={t('schoolYear.selectedDayNotInSchoolYear')}
        subtitle={t('schoolYear.selectedValideSchoolYear')}
      />
    );
  }

  return (
    <div className={styles['plan-stubstitutions']}>
      <div className={styles.header}>
        <CustomFilter entries={customFilterEntries} onFilterChange={handleFilterChange} />
        <div className={styles.count}>
          {!showAll
            ? t('substitutions.relevantCount', {
                count: filteredEvents.length,
                max: relevantMax.current,
              })
            : t('substitutions.allCount', {
                count: filteredEvents.length,
                max: events.length,
              })}
        </div>
        <ActionBar
          className={styles.bar}
          marginBottom='0'
          onGlobalFilterChange={(value) => setFilter(value)}
          showGlobalFilter={false}
          extendedActionsRight={
            <>
              <Button
                hierarchy='tertiary'
                icon={showAll ? <InvisibleIcon /> : <VisibleIcon />}
                onClick={() => {
                  substitutionStore.setSelectedRowIndex('0');
                  setShowAll(!showAll);
                }}
                className={styles['all-button']}
              >
                {showAll ? t('substitutions.showRelevant') : t('substitutions.showAll')}
              </Button>
              <Button
                hierarchy='tertiary'
                icon={<UploadIcon />}
                onClick={() => publishApp(dateString)}
                disabled={!perms?.canFetchAsVertretungsplanApp()}
              >
                {t('substitutions.publish')}
              </Button>
              <Dropdown
                trigger={
                  <Button hierarchy='tertiary' icon={<PrintIcon />} isLoading={loading}>
                    {t('common.print')}
                  </Button>
                }
              >
                <DropdownMenu
                  data={[
                    {
                      label: t('substitutions.normalReport'),
                      onClick: async () => {
                        setLoading(true);
                        try {
                          await downloadSubstitutionReport(currentSchoolYear?.uuid ?? '', selectedDayStartString);
                        } finally {
                          setLoading(false);
                        }
                      },
                    },
                    {
                      label: t('substitutions.studentReport'),
                      onClick: async () => {
                        setLoading(true);
                        try {
                          await downloadSubstitutionStudentReport(
                            currentSchoolYear?.uuid ?? '',
                            selectedDayStartString,
                          );
                        } finally {
                          setLoading(false);
                        }
                      },
                    },
                  ]}
                />
              </Dropdown>
            </>
          }
        />
      </div>

      <Grid useFormGap>
        <GridRow>
          <GridColumn width={9}>
            <SubstitutionEventsTable
              filter={filter}
              data={filteredEvents}
              selected={{ [substitutionStore.selectedRowIndex]: true }}
              onSubstitute={handleSubstitute}
              onCancelEvent={handleCancelEvent}
              onMergeEvent={handleMergeEvent}
              onWorkOrder={handleWorkOrder}
              onDifferentTime={handleDifferentTime}
              onRowSelect={handleRowSelection}
              onOutputChange={handleOutputChange}
            />
          </GridColumn>
          <GridColumn width={3} className={styles['right-col']}>
            <Card
              wrapperClass='mb-4'
              header={{
                headline: t('announcements.title', { count: 2 }),
                actions: [
                  {
                    callback: () => setAnnouncementsModalOpen(true),
                    icon: <EditIcon />,
                  },
                ],
              }}
              collapsibleOn='always'
              isCollapsed={announcementsCollapsed}
              onCollapse={() => setAnnouncementsCollapsed(!announcementsCollapsed)}
            >
              <TextArea
                className='mb-2'
                value={publicAnnouncement}
                label={t('announcements.public')}
                name='public'
                rows={6}
                isScrollable
                readonly
              />
              <TextArea
                value={internalAnnouncement}
                label={t('announcements.internal')}
                name='internal'
                rows={6}
                isScrollable
                readonly
              />
            </Card>
            <Card wrapperClass={styles.timetable}>
              <div className={styles['class-selection']}>
                <Button
                  hierarchy='tertiary'
                  icon={<ArrowLeftIcon />}
                  disabled={selectedClassEntries.length <= 1}
                  onClick={() => handleClassSelection('left')}
                />
                <div
                  className={classNames(styles['selected-class'], {
                    [styles.empty]: selectedClassEntries.length === 0,
                  })}
                >
                  {selectedClassEntries[selectedClassIndex]?.name ?? t('substitutions.noClassSelected')}
                </div>
                <Button
                  hierarchy='tertiary'
                  icon={<ArrowRightIcon />}
                  disabled={selectedClassEntries.length <= 1}
                  onClick={() => handleClassSelection('right')}
                />
              </div>
              <TimetableView
                showTimegrid
                start={timetableStart.toDate()}
                end={timetableEnd.toDate()}
                entries={timetableEntries}
                padding='s'
              />
            </Card>
          </GridColumn>
        </GridRow>
      </Grid>

      {selectedEvent && (
        <SubstitutionModal
          isOpen={substitutionModalOpen}
          timetableStart={normalizedTimetableStart}
          timetableEnd={normalizedTimetableEnd}
          currentEvent={selectedEvent}
          teacherEvents={teacherEventsRef.current}
          onClose={() => {
            setSubstitutionModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <CancelEventModal
          eventUuid={selectedEvent.uuid}
          substitutionComment={selectedEvent.substitutionComment ?? ''}
          isOpen={cancelEventModalOpen}
          onClose={() => {
            setCancelEventModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <MergeEventModal
          isOpen={mergeEventModalOpen}
          originTeachers={selectedEvent.teachers}
          originRooms={selectedEvent.rooms}
          eventStart={selectedEvent.start}
          eventEnd={selectedEvent.end}
          eventUuid={selectedEvent.uuid}
          substitutionComment={selectedEvent.substitutionComment ?? ''}
          timetableStart={timetableStart}
          timetableEnd={timetableEnd}
          timetableEntries={timetableEntries}
          onClose={() => {
            setMergeEventModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <WorkOrderModal
          eventUuid={selectedEvent.uuid}
          comment={selectedEvent.substitutionComment ?? ''}
          isOpen={workOrderModalOpen}
          originTeachers={selectedEvent.teachers}
          originRooms={selectedEvent.rooms}
          eventStart={selectedEvent.start}
          eventEnd={selectedEvent.end}
          onClose={() => {
            setWorkOrderModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      {selectedEvent && (
        <DifferentTimeModal
          isOpen={differentTimeModalOpen}
          originTeachers={selectedEvent.teachers}
          originRooms={selectedEvent.rooms}
          onClose={() => {
            setDifferentTimeModalOpen(false);
            selectEvent(null);
          }}
        />
      )}

      <SubstitutionAnnouncementsModal
        isOpen={announcementsModalOpen}
        onClose={() => setAnnouncementsModalOpen(false)}
      />
    </div>
  );
});
